import React from 'react';
import styled from 'styled-components';
import api from '../api/axiosConfig';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/* ================== PAGE WRAP ================== */

const Page = styled.div`
  min-height: 100vh;
  padding: clamp(12px, 2.5vw, 28px);
`;

const Wrap = styled.div`
  width: min(100%, 1800px);
  margin: 0 auto;
  display: grid;
  gap: clamp(14px, 2vw, 22px);
`;

/* ================== HERO ================== */

const Hero = styled.section`
  position: relative;
  border-radius: 28px;
  overflow: hidden;
  padding: clamp(34px, 4vw, 54px) clamp(18px, 3vw, 34px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  background:
    radial-gradient(1400px 700px at 50% 0%, rgba(255, 255, 255, 0.1), transparent 60%),
    linear-gradient(180deg, #0b1b2b, #071321);

  &::before {
    content: '';
    position: absolute;
    inset: -40% -10% auto -10%;
    height: 80%;
    background:
      radial-gradient(circle at 30% 45%, rgba(255, 255, 255, 0.12), transparent 45%),
      radial-gradient(circle at 70% 40%, rgba(255, 255, 255, 0.1), transparent 50%);
    opacity: 0.7;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: -20%;
    right: -20%;
    bottom: -55%;
    height: 80%;
    background: radial-gradient(closest-side, rgba(255, 138, 31, 0.35), transparent 70%);
    transform: rotate(-6deg);
    opacity: 0.55;
    pointer-events: none;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  gap: 10px;
  max-width: 760px;
`;

const Title = styled.h1`
  margin: 0;
  color: #fff;
  font-weight: 900;
  letter-spacing: -0.02em;
  font-size: clamp(32px, 4vw, 52px);
`;

const Subtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.6;
  font-size: clamp(14px, 1.6vw, 16px);
  white-space: pre-line;
`;

/* ================== GLASS BOARD ================== */

const Board = styled.section`
  border-radius: 26px;
  padding: clamp(14px, 2vw, 18px);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Grid = styled.div`
  display: grid;
  gap: clamp(12px, 1.6vw, 16px);

  @media (min-width: 980px) {
    grid-template-columns: 1.05fr 0.95fr;
    align-items: start;
  }
`;

const Col = styled.div`
  display: grid;
  gap: clamp(12px, 1.6vw, 16px);
`;

/* ================== CARD ================== */

const Card = styled.article`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 20px;
  padding: clamp(14px, 1.8vw, 18px);
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const CardTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 900;
  color: #0b1b2b;
`;

const P = styled.p`
  margin: 0;
  color: rgba(0, 0, 0, 0.72);
  line-height: 1.65;
  font-size: 14px;
  white-space: pre-line;
`;

const Muted = styled.p`
  margin: 8px 0 0;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.55;
  font-size: 13px;
  white-space: pre-line;
`;

/* ================== WORKFLOW ================== */

const WorkflowGrid = styled.div`
  display: grid;
  gap: 14px;

  @media (min-width: 620px) {
    grid-template-columns: 1fr 180px;
    align-items: center;
  }
`;

const Steps = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
`;

const Step = styled.li`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: start;
`;

const StepNum = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-weight: 900;
  font-size: 13px;
  color: #fff;
  background: linear-gradient(180deg, #ff8a1f, #e87412);
  box-shadow: 0 10px 20px rgba(255, 138, 31, 0.28);
`;

const StepBody = styled.div``;

const StepTitle = styled.div`
  font-weight: 900;
  color: #0b1b2b;
  font-size: 14px;
  margin: 0 0 2px;
`;

const StepText = styled.div`
  color: rgba(0, 0, 0, 0.62);
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-line;
`;

const Illo = styled.div`
  width: 170px;
  height: 170px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: inset 0 0 0 10px rgba(255, 255, 255, 0.55);
  display: grid;
  place-items: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 30% 35%, rgba(255, 138, 31, 0.25), transparent 50%),
    radial-gradient(circle at 70% 55%, rgba(80, 160, 255, 0.18), transparent 55%),
    linear-gradient(180deg, rgba(11, 27, 43, 0.1), rgba(11, 27, 43, 0.02));

  @media (max-width: 619px) {
    width: 140px;
    height: 140px;
    justify-self: start;
  }
`;

const IlloImg = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

/* ================== FEATURE LIST ================== */

const FeatureList = styled.div`
  margin-top: 10px;
  display: grid;
  gap: 10px;
`;

const Feature = styled.div`
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 10px;
  align-items: start;
`;

const Icon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: rgba(11, 27, 43, 0.06);
  border: 1px solid rgba(11, 27, 43, 0.08);
  font-size: 16px;
  line-height: 1;
`;

const FeatureTitle = styled.div`
  font-weight: 900;
  color: #0b1b2b;
  font-size: 14px;
  margin: 0 0 2px;
`;

const FeatureText = styled.div`
  color: rgba(0, 0, 0, 0.62);
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-line;
`;

/* ================== PIE CHART ================== */

const ChartRow = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 10px;

  @media (min-width: 720px) {
    grid-template-columns: 200px 1fr;
    align-items: center;
  }
`;

const PieWrap = styled.div`
  display: grid;
  place-items: center;
  position: relative;
`;

const Pie = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 999px;
  background: conic-gradient(#ff8a1f 0% 40%, #2e5676 40% 70%, #0b1b2b 70% 100%);
  position: relative;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.22);
  border: 10px solid rgba(255, 255, 255, 0.7);

  &::after {
    content: '';
    position: absolute;
    inset: 22%;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
  }
`;

const PieCenter = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  z-index: 2;
  text-align: center;
  font-weight: 900;
  color: #0b1b2b;
  font-size: 13px;
`;

const Legend = styled.div`
  display: grid;
  gap: 8px;
`;

const LegendTitle = styled.div`
  font-weight: 900;
  color: #0b1b2b;
  margin-bottom: 2px;
`;

const LegendItem = styled.div`
  display: grid;
  grid-template-columns: 12px 1fr auto;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.68);
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${(p) => p.$c || '#000'};
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
`;

/* ================== PRICING / SUPPORT ================== */

const Split = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 8px;

  @media (min-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MiniTitle = styled.div`
  font-weight: 900;
  color: #0b1b2b;
  margin-bottom: 6px;
`;

/* ================== BIO ================== */

const BioGrid = styled.div`
  display: grid;
  gap: 14px;
  align-items: center;

  @media (min-width: 720px) {
    grid-template-columns: 1fr 160px;
  }
`;

const Avatar = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 999px;
  justify-self: end;
  border: 10px solid rgba(255, 255, 255, 0.75);
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.2);
  display: grid;
  place-items: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 30% 30%, rgba(255, 138, 31, 0.2), transparent 55%),
    radial-gradient(circle at 70% 55%, rgba(80, 160, 255, 0.18), transparent 55%),
    linear-gradient(180deg, rgba(11, 27, 43, 0.1), rgba(11, 27, 43, 0.02));

  @media (max-width: 719px) {
    justify-self: start;
  }
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

/* ================== CTA ================== */

const CTA = styled.section`
  position: relative;
  border-radius: 26px;
  overflow: hidden;
  padding: 26px 18px;
  text-align: center;

  background:
    radial-gradient(900px 300px at 50% 0%, rgba(255, 255, 255, 0.1), transparent 65%),
    linear-gradient(180deg, #0b1b2b, #071321);

  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const CTATitle = styled.h2`
  margin: 0;
  color: #fff;
  font-weight: 900;
  font-size: 26px;
`;

const CTASub = styled.p`
  margin: 8px auto 0;
  max-width: 760px;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.6;
  font-size: 14px;
  white-space: pre-line;
`;

const CTAButton = styled.button`
  margin-top: 14px;
  border-radius: 14px;
  padding: 12px 20px;
  font-weight: 900;
  border: none;
  background: #ff8a1f;
  color: #1b1208;
  cursor: pointer;
  box-shadow: 0 18px 40px rgba(255, 138, 31, 0.22);
  transition:
    transform 160ms ease,
    filter 160ms ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }
`;

/* ================== helpers ================== */

const absMedia = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `http://127.0.0.1:8000${url}`;
};

const safe = (v, fallback = '') => (v === null || v === undefined ? fallback : v);

/* ✅ FIX "/api/api/..." */
function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

/* ================== PAGE ================== */

export default function AboutUs() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const lang = i18n.language?.startsWith('it') ? 'it' : 'en';
  const pick = (obj, key, fallback = '') => {
    if (!obj) return fallback;
    return (lang === 'it' ? obj[`${key}_it`] : obj[`${key}_en`]) || obj[`${key}_en`] || fallback;
  };

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await api.get(safeApiPath('/aboutus/page/'));
        if (!alive) return;
        setData(res.data);
      } catch (e) {
        console.error('AboutUs load error:', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const d = data || {};

  const features = [
    {
      icon: safe(d.feature1_icon, '💬'),
      title: pick(d, 'feature1_title'),
      text: pick(d, 'feature1_text'),
    },
    {
      icon: safe(d.feature2_icon, '🏢'),
      title: pick(d, 'feature2_title'),
      text: pick(d, 'feature2_text'),
    },
    {
      icon: safe(d.feature3_icon, '📲'),
      title: pick(d, 'feature3_title'),
      text: pick(d, 'feature3_text'),
    },
    {
      icon: safe(d.feature4_icon, '🧭'),
      title: pick(d, 'feature4_title'),
      text: pick(d, 'feature4_text'),
    },
  ];

  const illoUrl = absMedia(d.illustration_url);
  const avatarUrl = absMedia(d.avatar_url);

  return (
    <Page>
      <SEO title="About Us" description="Learn more about our company and team." path="/about-us" />
      <Wrap>
        <Hero>
          <HeroInner>
            <Title>{loading ? '...' : pick(d, 'hero_title', 'About Us')}</Title>
            <Subtitle>{loading ? '' : pick(d, 'hero_subtitle', '')}</Subtitle>
          </HeroInner>
        </Hero>

        <Board>
          <Grid>
            {/* LEFT COLUMN */}
            <Col>
              <Card>
                <CardTitle>{pick(d, 'about_project_title', 'About this project')}</CardTitle>
                <P>{pick(d, 'about_project_text', '')}</P>
              </Card>

              <Card>
                <CardTitle>{pick(d, 'workflow_title', 'How we will work together')}</CardTitle>

                <WorkflowGrid>
                  <Steps>
                    <Step>
                      <StepNum>1</StepNum>
                      <StepBody>
                        <StepTitle>{pick(d, 'step1_title', 'Discussion')}</StepTitle>
                        <StepText>{pick(d, 'step1_text', '')}</StepText>
                      </StepBody>
                    </Step>

                    <Step>
                      <StepNum>2</StepNum>
                      <StepBody>
                        <StepTitle>{pick(d, 'step2_title', 'Structure')}</StepTitle>
                        <StepText>{pick(d, 'step2_text', '')}</StepText>
                      </StepBody>
                    </Step>

                    <Step>
                      <StepNum>3</StepNum>
                      <StepBody>
                        <StepTitle>{pick(d, 'step3_title', 'Design')}</StepTitle>
                        <StepText>{pick(d, 'step3_text', '')}</StepText>
                      </StepBody>
                    </Step>

                    <Step>
                      <StepNum>4</StepNum>
                      <StepBody>
                        <StepTitle>{pick(d, 'step4_title', 'Launch')}</StepTitle>
                        <StepText>{pick(d, 'step4_text', '')}</StepText>
                      </StepBody>
                    </Step>

                    <Step>
                      <StepNum>5</StepNum>
                      <StepBody>
                        <StepTitle>{pick(d, 'step5_title', 'Support')}</StepTitle>
                        <StepText>{pick(d, 'step5_text', '')}</StepText>
                      </StepBody>
                    </Step>
                  </Steps>

                  <Illo aria-label="Illustration">
                    {illoUrl ? <IlloImg src={illoUrl} alt="Illustration" /> : 'Illustration'}
                  </Illo>
                </WorkflowGrid>
              </Card>

              <Card>
                <CardTitle>{pick(d, 'startup_title', 'Startup Growth')}</CardTitle>
                <P>{pick(d, 'startup_text', '')}</P>

                <ChartRow>
                  <PieWrap>
                    <Pie />
                    <PieCenter>
                      {pick(d, 'chart_center_line1', 'Project')}
                      <br />
                      {pick(d, 'chart_center_line2', 'Growth')}
                    </PieCenter>
                  </PieWrap>

                  <Legend>
                    <LegendTitle>{pick(d, 'legend_title', 'Startup Growth')}</LegendTitle>

                    <LegendItem>
                      <Dot $c="#ff8a1f" />
                      <span>{pick(d, 'legend1_label', 'Failing')}</span>
                      <strong>{safe(d.legend1_value, '40%')}</strong>
                    </LegendItem>

                    <LegendItem>
                      <Dot $c="#2e5676" />
                      <span>{pick(d, 'legend2_label', 'Steady')}</span>
                      <strong>{safe(d.legend2_value, '30%')}</strong>
                    </LegendItem>

                    <LegendItem>
                      <Dot $c="#0b1b2b" />
                      <span>{pick(d, 'legend3_label', 'Growing')}</span>
                      <strong>{safe(d.legend3_value, '30%')}</strong>
                    </LegendItem>

                    <Muted>{pick(d, 'legend_note', '')}</Muted>
                  </Legend>
                </ChartRow>
              </Card>
            </Col>

            {/* RIGHT COLUMN */}
            <Col>
              <Card>
                <CardTitle>{pick(d, 'who_title', 'Who I am & how I work')}</CardTitle>

                <FeatureList>
                  {features.map((f, idx) => (
                    <Feature key={idx}>
                      <Icon aria-hidden="true">{safe(f.icon, '⭐️')}</Icon>
                      <div>
                        <FeatureTitle>{safe(f.title)}</FeatureTitle>
                        <FeatureText>{safe(f.text)}</FeatureText>
                      </div>
                    </Feature>
                  ))}
                </FeatureList>
              </Card>

              <Card>
                <CardTitle>
                  {pick(d, 'not_just_title', 'I don’t create just beautiful websites')}
                </CardTitle>
                <P>{pick(d, 'not_just_text', '')}</P>
              </Card>

              <Card>
                <CardTitle>
                  {pick(d, 'pricing_title', 'Transparent pricing & ongoing support')}
                </CardTitle>

                <Split>
                  <div>
                    <MiniTitle>{pick(d, 'pricing_left_title', 'Transparent pricing')}</MiniTitle>
                    <P>{pick(d, 'pricing_left_text', '')}</P>
                  </div>

                  <div>
                    <MiniTitle>{pick(d, 'pricing_right_title', 'Ongoing support')}</MiniTitle>
                    <P>{pick(d, 'pricing_right_text', '')}</P>
                  </div>
                </Split>
              </Card>

              <Card>
                <CardTitle>{pick(d, 'bio_title', 'A few words about me')}</CardTitle>

                <BioGrid>
                  <div>
                    <P>{pick(d, 'bio_text', '')}</P>
                    <Muted>{pick(d, 'bio_muted', '')}</Muted>
                  </div>

                  <Avatar aria-label="Avatar">
                    {avatarUrl ? <AvatarImg src={avatarUrl} alt="Avatar" /> : 'Avatar'}
                  </Avatar>
                </BioGrid>
              </Card>
            </Col>
          </Grid>
        </Board>

        <CTA>
          <CTATitle>{pick(d, 'cta_title', "Let's talk")}</CTATitle>
          <CTASub>{pick(d, 'cta_subtitle', '')}</CTASub>
          <CTAButton onClick={() => navigate('/contacts')}>
            {pick(d, 'cta_btn_text', 'Contact Me')}
          </CTAButton>
        </CTA>
      </Wrap>
    </Page>
  );
}
